import { AxiosError } from 'axios'

import {
  IOPAPostMessage,
  OPAErrorCode,
  OPAMessagingConstants
} from './PatientAccessForm.models'
import {
  getDependencyErrorCode,
  getDependencyErrorMessage
} from '../../utils/getDependencyErrorMessage'

export const OPA_LINK_ORACLE_REJECTION_TITLE = 'Unable to start this order'

export interface IOpaLinkErrorAnnouncement {
  title?: string
  description: string
}

/** True when Oracle refused the OPA session because another user holds this patient's form. */
export const isOpaPatientLockedError = (error: AxiosError): boolean =>
  getDependencyErrorCode(error) === OPAErrorCode.XXCL_OPA_INIT_PATIENT_LOCKED

const BFF_VALIDATION_ERROR_TYPE =
  'urn:clinigen:onlineservices:errors:validation-error'

/** The reason Clinigen Direct itself refused the request (e.g. a PTA rule), if it did. */
export const getBffValidationReason = (
  error: AxiosError
): string | undefined => {
  const data = error?.response?.data as
    | { type?: unknown; detail?: unknown }
    | undefined
  return data?.type === BFF_VALIDATION_ERROR_TYPE &&
    typeof data.detail === 'string' &&
    data.detail.trim()
    ? data.detail
    : undefined
}

/**
 * CTT-1483: when Oracle refuses to start an order (another order in progress,
 * no access to the program, ...) show its reason rather than an OPA error.
 * The same applies when Clinigen Direct refuses it before Oracle is called
 * (PTA consent/screening rule, SIT 22 Sep). The OPA wording is only the
 * fallback when nobody gave a reason.
 */
export const getOpaLinkErrorAnnouncement = (
  error: AxiosError
): IOpaLinkErrorAnnouncement => {
  const reason =
    getDependencyErrorMessage(error) ?? getBffValidationReason(error)

  if (reason) {
    return {
      title: OPA_LINK_ORACLE_REJECTION_TITLE,
      description: reason
    }
  }

  return { description: `There was an error fetching OPA URL. ${error}` }
}

/** Max silence from the OPA iframe (no postMessage) before warning that submission is slow (EBS can be slow in DEV). */
export const OPA_SUBMISSION_TIMEOUT_MS = 5 * 60 * 1000

export interface IOpaPostMessageLogContext {
  programId: string
  physicianId: string
}

export const isOpaPostMessage = (data: unknown): data is IOPAPostMessage =>
  typeof data === 'object' &&
  data !== null &&
  ((data as IOPAPostMessage).type === OPAMessagingConstants.OpaOnlineServices ||
    (data as IOPAPostMessage).event ===
      OPAMessagingConstants.OpaOnlineServices ||
    (data as IOPAPostMessage).system ===
      OPAMessagingConstants.ServiceCloudCPOPA)

export const logOpaPostMessage = (
  message: IOPAPostMessage,
  context: IOpaPostMessageLogContext
): void => {
  console.warn('[OPA postMessage]', {
    programId: context.programId,
    physicianId: context.physicianId,
    type: message.type,
    event: message.event,
    system: message.system,
    status: message.status,
    order: message.order,
    errorCode: message.errorCode,
    errorMessage: message.errorMessage,
    message: message.message
  })
}

export const isTerminalOpaPostMessage = (message: IOPAPostMessage): boolean =>
  Boolean(
    (message.status && message.order) ||
      (message.errorCode && !message.status) ||
      message.message === 'Save & Exit'
  )

export interface IOpaSilenceWatch {
  onOpaMessage: (message: IOPAPostMessage) => void
  stop: () => void
}

/**
 * Calls onSilence when the OPA iframe has sent nothing for timeoutMs. Every message
 * restarts the clock, so time spent filling in the form never counts; a terminal
 * message (order created, error, Save & Exit) ends the watch.
 */
export const watchOpaSilence = (
  onSilence: () => void,
  timeoutMs: number = OPA_SUBMISSION_TIMEOUT_MS
): IOpaSilenceWatch => {
  let isStopped = false
  let timeoutId: ReturnType<typeof setTimeout> | undefined

  const stop = () => {
    isStopped = true
    clearTimeout(timeoutId)
  }

  const restart = () => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => {
      if (!isStopped) {
        onSilence()
      }
    }, timeoutMs)
  }

  restart()

  return {
    onOpaMessage: (message) => {
      if (isStopped) {
        return
      }
      if (isTerminalOpaPostMessage(message)) {
        stop()
        return
      }
      restart()
    },
    stop
  }
}
